import styled from "styled-components"
import { useState } from "react"
import EditTextContainer from "./EditTextContainer"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons"
import EditDropdownContainer from "./EditDropdownContainer"
import { useUser } from "../../../context/useUser"
import { Dispatch, FC, SetStateAction } from "react"
import { GenericUpdateDTO, User } from "../../../models/user"
import UserService from "../../../services/user"
import useNotyf from "../../../context/useNotify"
import HelperService from "../../../services/helper"
import {formatLiveLinkUrl} from '../../../utils/formatString';

interface Props {
  player: User
  editProfile: boolean
  setEditProfile: Dispatch<SetStateAction<boolean>>
}

const Details: FC<Props> = ({ player, editProfile, setEditProfile }) => {
  const { user, setUser } = useUser()
  const notyf = useNotyf()

  // Data
  const youtubeInfo = formatLiveLinkUrl(player.youtube);
  const twitchInfo = formatLiveLinkUrl(player.twitch);

  const [editUsername, setEditUsername] = useState(false)
  const [editYTID, setEditYTID] = useState(false)
  const [editTwitchID, setEditTwitchID] = useState(false)

  // API
  const updateUser = async (updateObj: any) => {
    const dto: GenericUpdateDTO = { id: player._id, updateObj: updateObj }
    try {
      await UserService.genericUpdate(dto)
      notyf.success("Your details were updated successfully..")
    } catch (err) {
      console.error(`Error updating details: ${err}`)
      HelperService.showNotfyErr(notyf, err, "An error occurred updating your details.")
    }
  }

  const fetchUser = async (userId: string) => {
    try {
      const { user } = await UserService.checkToken()
      setUser(user)
    } catch (err) {
      console.error(`Error fetching user: ${err}`)
      HelperService.showNotfyErr(notyf, err, "An error occurred fetching the user.")
    }
  }

  // Methods

  // edit overview button
  const handleClickEditProfile = () => {
    setEditProfile(!editProfile)
  }

  // edit username
  const handleClickEditUsername = () => {
    setEditUsername(true)
  }
  const handleCloseEditUsername = () => {
    setEditUsername(false)
  }
  const handleSubmitEditUsername = async (newUsername: string) => {
    if (!newUsername) return
    if (!user) return
    await updateUser({ username: newUsername })
    await fetchUser(user._id)
  }

  // edit yt id
  const handleClickEditYTID = () => {
    setEditYTID(true)
  }
  const handleCloseEditYTID = () => {
    setEditYTID(false)
  }
  const handleSubmitEditYTID = async (newYTID: string) => {
    if (!user) return
    await updateUser({ youtube: newYTID })
    await fetchUser(user._id)
  }
  
  // edit twitch id
  const handleClickEditTwitchID = () => {
    setEditTwitchID(true)
  }
  const handleCloseEditTwitchID = () => {
    setEditTwitchID(false)
  }
  const handleSubmitEditTwitchID = async (newTwitchID: string) => {
    if (!user) return
    await updateUser({ twitch: newTwitchID })
    await fetchUser(user._id)
  }

  // edit position
  const [editPosition, setEditPosition] = useState(false)
  const handleClickEditPosition = () => {
    setEditPosition(true)
  }
  const handleCloseEditPosition = () => {
    setEditPosition(false)
  }
  const handleSubmitEditPosition = async (newPosition: string) => {
    if (!user) return
    if (!newPosition) return
    await updateUser({ position: newPosition })
    await fetchUser(user._id)
  }

  return (
    <Wrapper>
      <Container className="profile-side-container gradient-gray-light black-border">
        <HeaderContainer className="gradient-gray heading ">
          <div style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
            <HeaderText className="fw-bold">DETAILS</HeaderText>
          </div>
          {/* open edit mode */}
          {user?._id === player._id ? (
            <FaIcon onClick={handleClickEditProfile}>
              <FontAwesomeIcon
                icon={faPenToSquare}
                style={{ cursor: "pointer" }}
              />
            </FaIcon>
          ) : null}
        </HeaderContainer>

        <BodyContainer>
          {/* username */}
          <TextContainer>
            {editUsername && (
              <EditTextContainer
                title={"EDIT USERNAME"}
                closeFunc={handleCloseEditUsername}
                submitFunc={handleSubmitEditUsername}
              />
            )}
            <Title className="heading fw-bold">EA USERNAME</Title>
            {editProfile && (
              <EditButton onClick={handleClickEditUsername}>
                <FontAwesomeIcon
                  icon={faPenToSquare}
                  style={{ cursor: "pointer" }}
                />
              </EditButton>
            )}
            <Text>{player.username}</Text>
          </TextContainer>
          {/* yt */}
          <TextContainer>
            {editYTID && (
              <EditTextContainer
                title={"EDIT YOUTUBE CHANNEL"}
                closeFunc={handleCloseEditYTID}
                submitFunc={handleSubmitEditYTID}
              />
            )}
            <Title className="heading fw-bold">YOUTUBE</Title>
            {editProfile && (
              <EditButton onClick={handleClickEditYTID}>
                <FontAwesomeIcon
                  icon={faPenToSquare}
                  style={{ cursor: "pointer" }}
                />
              </EditButton>
            )}
        <Text>
          {youtubeInfo.url ? (
            <a href={youtubeInfo.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-reset">
              {youtubeInfo.displayName}
            </a>
          ) : (
            youtubeInfo.displayName || 'N/A'
          )}
        </Text>
          </TextContainer>
          {/* twitch */}
          <TextContainer>
            {editTwitchID && (
              <EditTextContainer
                title={"EDIT TWITCH CHANNEL"}
                closeFunc={handleCloseEditTwitchID}
                submitFunc={handleSubmitEditTwitchID}
              />
            )}
            <Title className="heading fw-bold">TWITCH</Title>
            {editProfile && (
              <EditButton onClick={handleClickEditTwitchID}>
                <FontAwesomeIcon
                  icon={faPenToSquare}
                  style={{ cursor: "pointer" }}
                />
              </EditButton>
            )}
        <Text>
          {twitchInfo.url ? (
            <a href={twitchInfo.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-reset">
              {twitchInfo.displayName}
            </a>
          ) : (
            twitchInfo.displayName || 'N/A'
          )}
        </Text>
          </TextContainer>
          {/* position at bottom for tablet */}
          <FooterContainer className="tablet-position-bottom">
            {editPosition && (
              <EditDropdownContainer
                title={"EDIT POSITION"}
                closeFunc={handleCloseEditPosition}
                submitFunc={handleSubmitEditPosition}
                dropdownTitle={"Select position"}
              />
            )}
            <FooterTextContainer>
              <Title className="heading fw-bold">POSITION</Title>
              {editProfile && (
                <EditButton onClick={handleClickEditPosition}>
                  <FontAwesomeIcon
                    icon={faPenToSquare}
                    style={{ cursor: "pointer" }}
                  />
                </EditButton>
              )}
            </FooterTextContainer>
            <FooterBoxContainer>
              <FooterBox className="gradient-gray black-border">
                {player.position || "N/A"}
              </FooterBox>
            </FooterBoxContainer>
          </FooterContainer>
        </BodyContainer>
      </Container>
    </Wrapper>
  )
}

export default Details

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 1rem 2rem;
  @media (max-width: 1200px) {
    padding: 1rem 1rem;
  }
  @media (max-width: 992px) {
    width: 350px;
  } 
  @media (max-width: 768px) {
  width: 100%;
  height: 100%;  
  padding-top: 0;
  }
  /* outline: solid red 3px; */
`
const Container = styled.div`
  height: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  background: linear-gradient(120deg, #000000ff 60%, #040923ff 100%);
  border: 2px solid #5fa8e6;
  box-shadow: 0 0 5px 1px #00eaff;
  border-radius: 18px;
  padding: 1.5rem 1.2rem;
  position: relative;
  @media (max-width: 992px) {
    flex-direction: column;
    gap: 1rem;
    width: 320px;
  }
    
`
//header
const HeaderContainer = styled.div`
      color: white;
      background: linear-gradient(135deg, #23263a 60%, #1a1c28 100%);
      padding: 0.7rem 1.2rem;
      position: relative;
      border-top: 0.5px solid #5fa8e6;
      border-bottom: 0.5px solid #5fa8e6;
      box-shadow: 0 0 12px 2px #00eaff33;
      border-radius: 0;
      min-height: 40px;
      height: 40px;
      display: flex;
      align-items: center;
`
const HeaderText = styled.h4`
  margin: 0;
  padding: 0;
  display: inline-block;
`
const FaIcon = styled.div`
  color: lightBlue;
  /* padding: 1rem 0; */
  position: absolute;
  top: 0;
  left: 0;
  margin: 0.5rem;
`
//body
const BodyContainer = styled.div`
  color: white;
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  @media (max-width: 992px) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: stretch;
  }
`
const TextContainer = styled.div`
  padding: 0.5rem 0;
`
const Title = styled.p`
  margin: 0;
  padding: 0;
  font-weight: 400;
  color: white;
  display: inline-block;
`
const Text = styled.h6`
  margin: 0;
  padding: 0;
  /* font-weight: 600; */
`
//footer
const FooterContainer = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 0.5rem;
  @media (max-width: 992px) {
    margin-top: 0rem;
    margin-bottom: 0;
    align-self: center;
  }
  @media (max-width: 768px) {
    align-self: center;
    margin-top: 2rem;
    margin-bottom: 0;
  }
`;

const FooterTextContainer = styled.div`
  display: inline-block;
`
const FooterBoxContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`
const FooterBox = styled.div`
  color: white;
  font-size: 50px;
  padding: 0.25rem 0;
  background: linear-gradient(120deg, #0e1124ff 60%, #0c0f1eff 100%);
  border: 2px solid #5fa8e6;
  box-shadow: 0 0 12px 1px #00eaff;
  border-radius: 8px;
  width: 140px;
  text-transform: uppercase;
`
const EditButton = styled.button`
  margin-left: 1rem;
  border: none;
  background-color: rgba(0, 0, 0, 0);
  color: white;
`
